/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-02-14 00:00:00
 *
 *******************************************************/
import _ from 'lodash';
import moment from 'moment';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Page,
  Button,
  Layout,
  Card,
  Badge,
  Stack,
  ButtonGroup,
  Icon,
  Link,
  Tooltip,
  TextStyle,
  SkeletonBodyText,
  SkeletonDisplayText
} from '@shopify/polaris';
import {
  ExternalMinor,
  ReadTimeMinor,
  DuplicateMinor,
  ThemesMajor,
  SettingsMajor,
  DeleteMajor
} from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { routes } from 'src/routes';
import history from 'src/routes/history';
import { URL } from 'src/constants/links';

import ThemeNotExist from 'src/components/containers/shopify-integration/theme-not-exist';
import { showSABToast } from 'src/integrations/shopify-app-bridge/toast';
import SelectTemplateModal from 'src/components/containers/shopify-integration/select-template.modal';
import TemplateChangedModal from 'src/components/containers/shopify-integration/template-changed.modal';
import CustomizeTemplateModal from 'src/components/containers/shopify-integration/manage-template-instance/modal/customize-template.modal';
import PopOverActionButton from 'src/components/views/shopify-integration/popover-action-button';
import WarningAddWidgetLiveThemeModal from 'src/components/views/shopify-integration/warning-add-widget-live-theme-modal';
import ErrorLimitSectionModal from 'src/components/views/shopify-integration/error-limit-section-modal';
import ErrorModal from 'src/components/views/shopify-integration/error-modal';
import AddWidgetThemeSuccessModal from 'src/components/views/shopify-integration/add-widget-theme-success-modal';
import EnableWidgetSuccessModal from 'src/components/views/shopify-integration/enable-widget-success-modal';
import EnableWidgetFailModal from 'src/components/views/shopify-integration/enable-widget-fail-modal';
import WidgetAlreadyAddedModal from 'src/components/views/shopify-integration/widget-already-added-modal';

import {
  navigateToShopifyCustomizeSSR,
  copyClipBoard
} from 'src/helpers/shopify-integration-helpers';
import { trackFSEvent } from 'src/integrations/fullStory';
import { getFullAppUrlV2 } from 'src/helpers';

import ThumbnailDefaultTemplate from 'src/assets/images/shopify-integration/ThumbnailDefaultTemplate.png';
import ThumbnailBasicTemplate from 'src/assets/images/shopify-integration/ThumbnailBasicTemplate.png';
import BoostLogo from 'src/assets/images/shopify-integration/BoostLogo.png';

import {
  LIMIT_CUSTOM_TEMPLATE_INSTANCE,
  WIDGET_FEATURES,
  TEMPLATE_FEATURE,
  ERROR_CODE
} from 'src/constants/shopify-integration';
import { SI_THEME_INTEGRATION_FEATURE_SUCCESS } from 'src/stores/flow-type/shopify-integration/theme-integration.type';

import * as shopifyIntegrationThemeAction from 'actions/shopify-integration/theme-actions';
import * as templateInstanceAction from 'actions/shopify-integration/template-instance-actions';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';

type Props = {};
type ComposedProps = Props & WithI18nProps;

type State = {};

class ShopifyIntegrationScreenUpdate extends Component<ComposedProps, State> {
  state = {
    refreshingThemeList: false,
    openModal: false,
    openTemplateChangedModal: false,
    integratedTemplateInstance: {},
    openCustomizeModal: false,

    isLoadingAddCollection: false,
    isLoadingAddSearch: false,
    isEnablingISW: false,
    openWarningAddWidgetLiveTheme: false,
    openErrorLimitSection: false,
    openErrorModal: false,
    openAddWidgetSuccess: false,
    openEnableWidgetSuccess: false,
    openEnableWidgetFail: false,
    openWidgetAlreadyAdded: false
    // Hardcode for testing
    // integratedTemplateInstance: {
    //   templateId: 'pZDpaYLJg',
    //   templateVersion: '24.0.0',
    //   templateName: 'Copy of Default 32',
    //   themeLibVersion: '1.0.0',
    //   themeNameLib: 'default',
    //   widgetIds: {
    //     collectionFilters: 'ftpl_26e23c81-2122-48d9-b253-0d1b296d58d8',
    //     productList: 'prdgrid_9e1c911e-c323-46fe-90d9-2abe0ba64c48',
    //     productItem: 'prditem_7ae2312c-ab81-43ba-b2b6-200152fc82fb',
    //     collectionHeader: 'ch_528c8095-5bd9-47c7-a490-05923b6bbd5e',
    //     searchPage: 'sp_4899d2b8-283a-42e6-a11c-9638ae6f4c1c',
    //     instantSearchWidget: '685a6f4c-c77c-4e6b-94e1-a9fb4bfa8a87',
    //     quickView: 'qv_aa8e9bd9-6b6a-467e-85f4-983d51dbb769',
    //     addToCart: 'atc_078ac406-def0-4746-af10-428670642687',
    //     placeholderFilterTree: 'pl_ftpl_ec687df0-888a-4743-8216-89223f36a622'
    //   },
    //   tenantId: 'bc-dev-v2-phuong-5.myshopify.com',
    //   themeIds: [126973313105],
    //   refThemeId: 126973313105,
    //   clonedFromTemplateId: 'GL-C6iI3K',
    //   clonedFromDefault: true,
    //   createdAt: 1710728733
    // }
  };
  hashTemplateUpdate = {};

  async componentDidMount() {
    // console.log('ENV', process.env.NODE_ENV);
    const { getBoostTemplateInstances, getTemplateInstances, themes = [], match } = this.props;

    // Save integratedTemplateInstance to State
    const response = await this.props.checkThemeIntegrationStatus(match.params.themeId);
    const result = _.get(response, 'payload.data', {});
    if (!_.isEmpty(result) && result.isDeleted) return;

    this.setState({ integratedTemplateInstance: result });

    // Get Boost template instances
    getBoostTemplateInstances();

    // Get Template instances settings on theme
    const resultTemplates = await getTemplateInstances();
    const templateInstances = _.get(resultTemplates, 'payload.data', []);
    const { templateId } = result;
    const hasUpdate = Boolean(_.find(templateInstances, { templateId })?.hasUpdate) || false;
    this.setState({
      hasUpdate
    });

    if (!themes.length) {
      this.props.getAllThemes();
    }
  }

  componentWillReceiveProps(nextProps) {
    const { themes } = this.props;
    const { themes: nextThemes, updateFieldReducer, match } = nextProps;
    if (themes !== nextThemes) {
      const themeId = Number(match.params.themeId) || '';
      const themeInfo = nextThemes.find(item => item.id === themeId);
      if (themeInfo) {
        updateFieldReducer('selectedThemeId', themeInfo?.id);
      }
    }
  }

  render() {
    const {
      openModal,
      openTemplateChangedModal,
      integratedTemplateInstance,
      openCustomizeModal,
      openWarningAddWidgetLiveTheme,
      openErrorLimitSection,
      openErrorModal,
      openAddWidgetSuccess,
      openEnableWidgetSuccess,
      openEnableWidgetFail,
      openWidgetAlreadyAdded
    } = this.state;
    const {
      boostTemplates = [],
      templateInstances = [],
      integratingTheme = false,
      themes = [],
      selectedThemeId,
      match,
      isThemeDeleted,
      duplicateTemplateLoading,
      emailSupport
    } = this.props;

    if (isThemeDeleted) return <ThemeNotExist />;

    // Move selected template instance to top of list
    const sortedTemplateInstances = templateInstances;
    if (!_.isEmpty(integratedTemplateInstance)) {
      const [templateInstance] = _.remove(sortedTemplateInstances, {
        templateId: integratedTemplateInstance.templateId
      });

      if (templateInstance) sortedTemplateInstances.unshift(templateInstance);
    }

    // Get selected theme details
    const selectedThemeDetails = _.find(themes, { id: Number(match.params.themeId) });
    const templateId = _.get(integratedTemplateInstance, 'templateId', '');
    const isOS20 = _.get(selectedThemeDetails, 'isOS20', '');

    return (
      <Page
        title={
          <div className="theme-setup-heading">
            <Stack spacing="tight">{this.renderPageTitle()}</Stack>
          </div>
        }
        breadcrumbs={[
          {
            content: '',
            // Back to theme selection page
            onAction: this.handleGoBack
          }
        ]}>
        <div className="integration-layout">
          <Layout>
            <Layout.Section>
              <Card>
                <Card.Section>
                  {/* Confirm select instance */}
                  {this.renderConfirmSelectInstance()}

                  {/* Widget integration with App Embed */}
                  {/* {this.renderWidgetIntegrationWithAppEmbed()} */}

                  {/* Guide setup widgets */}
                  {this.renderGuideSetupWidgets()}
                </Card.Section>
              </Card>
            </Layout.Section>
          </Layout>
        </div>

        <SelectTemplateModal
          open={openModal}
          disabling={integratingTheme}
          onClose={this.handleChangeModal}
          onConfirm={this.handleConfirmModal}
          integratedTemplateInstance={integratedTemplateInstance}
          boostTemplates={boostTemplates}
          templateInstances={sortedTemplateInstances}
          selectedTheme={selectedThemeDetails}
          match={match}
        />

        <TemplateChangedModal
          open={openTemplateChangedModal}
          onClose={this.handleChangeTemplateModal}
          integratedTemplateInstance={integratedTemplateInstance}
          selectedTheme={selectedThemeDetails}
        />

        <CustomizeTemplateModal
          open={openCustomizeModal}
          internalNavigation
          loading={duplicateTemplateLoading || integratingTheme}
          onClose={this.handleCustomizeTemplateModal}
          onConfirm={this.handleConfirmCustomizeModal}
          templateInstance={integratedTemplateInstance}
          isReachLimitCustomTemplate={templateInstances.length >= LIMIT_CUSTOM_TEMPLATE_INSTANCE}
        />

        <WarningAddWidgetLiveThemeModal
          open={openWarningAddWidgetLiveTheme}
          onClose={this.handleCloseWarningAddWidgetLive}
          onConfirm={this.handleAddWidget}
          feature={this.pathId}
        />

        <ErrorLimitSectionModal
          open={openErrorLimitSection}
          selectedThemeId={selectedThemeId}
          onClose={this.handleCloseErrorLimitSection}
          feature={this.pathId}
        />

        <ErrorModal
          open={openErrorModal}
          selectedThemeId={selectedThemeId}
          templateId={templateId}
          onClose={this.handleCloseErrorModal}
          emailSupport={emailSupport}
          feature={this.pathId}
        />

        <AddWidgetThemeSuccessModal
          open={openAddWidgetSuccess}
          selectedThemeId={selectedThemeId}
          templateId={templateId}
          onClose={this.handleCloseSuccessModal}
          emailSupport={emailSupport}
          feature={this.pathId}
          isOS20={isOS20}
        />

        <EnableWidgetSuccessModal
          open={openEnableWidgetSuccess}
          selectedThemeId={selectedThemeId}
          onClose={this.handleCloseEnableWidgetSuccessModal}
          emailSupport={emailSupport}
        />

        <EnableWidgetFailModal
          open={openEnableWidgetFail}
          templateId={templateId}
          selectedThemeId={selectedThemeId}
          onClose={this.handleCloseEnableWidgetFailModal}
          emailSupport={emailSupport}
        />

        <WidgetAlreadyAddedModal
          open={openWidgetAlreadyAdded}
          selectedThemeId={selectedThemeId}
          onClose={this.handleCloseWidgetAlreadyAdded}
          feature={this.pathId}
          isOS20={isOS20}
        />
      </Page>
    );
  }

  renderPageTitle = () => {
    const {
      i18n,
      themes = {},
      match,
      checkingTheme,
      getAllThemesLoading,
      getTemplateInstancesLoading
    } = this.props;
    const isLoading = checkingTheme || getAllThemesLoading || getTemplateInstancesLoading;

    // Get theme details
    const themeDetails = _.find(themes, { id: Number(match.params.themeId) });
    const themeName = _.get(themeDetails, 'name', '');
    const themeRole = _.get(themeDetails, 'role', '');
    const isOS20 = _.get(themeDetails, 'isOS20', '');
    const OSText = isOS20 ? '(OS 2.0)' : '(OS 1.0)';

    const badgeThemeRole =
      themeRole === 'main' ? (
        <Badge status="info">
          {i18n.translate('AdminHTML.ShopifyIntegration.ThemeStatus.liveTheme')}
        </Badge>
      ) : (
        <Badge>{i18n.translate('AdminHTML.ShopifyIntegration.ThemeStatus.unpublishedTheme')}</Badge>
      );

    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', minWidth: isLoading ? '250px' : 'unset' }}>
        {isLoading ? (
          <SkeletonDisplayText maxWidth="100%" />
        ) : (
          <>
            {themeName} {OSText} {badgeThemeRole}
          </>
        )}
      </div>
    );
  };

  renderConfirmSelectInstance = () => {
    const {
      i18n,
      selectedThemeId,
      upgradingTheme = false,
      boostTemplates = [],
      getAllThemesLoading,
      checkingTheme,
      themes,
      getTemplateInstancesLoading
    } = this.props;
    const { integratedTemplateInstance, hasUpdate } = this.state;
    const isLoading = checkingTheme || getAllThemesLoading || getTemplateInstancesLoading;

    const thumbnails = {
      default: ThumbnailDefaultTemplate,
      dawn: ThumbnailBasicTemplate
    };

    const { templateName, templateVersion, themeNameLib, createdAt, updatedAt, templateId } =
      integratedTemplateInstance;
    let lastSavedAt = updatedAt || createdAt;
    lastSavedAt = moment(lastSavedAt * 1000).format('MMM DD, YYYY [at] h:mm A');

    const boostTemplate = _.find(boostTemplates, { themeNameLib });
    const boostTemplateVersion = _.get(boostTemplate, 'templateVersion', '1.0.0');

    return (
      <div className="confirm-select-instance">
        {/* Heading */}
        <div className="heading">
          {i18n.translate('AdminHTML.ShopifyIntegration.UpdateInstance.confirmHeading')}
        </div>

        {/* Title */}
        <div className="title">
          <TextStyle variation="subdued">
            {i18n.translate('AdminHTML.ShopifyIntegration.SelectTemplate.NewTemplate')}
          </TextStyle>
        </div>

        {isLoading ? (
          <div
            style={{
              marginBottom: '24px',
              paddingBottom: '24px',
              borderBottom: '1px solid #e1e3e5'
            }}>
            <SkeletonBodyText />
          </div>
        ) : (
          <div className="instance page-update">
            {/* Image thumbnail represents specific theme lib */}
            <img
              className="thumbnail"
              src={thumbnails[themeNameLib]}
              width={90}
              height={72}
              alt=""
            />

            {/* Template info */}
            <div className="details">
              <div className="info-title">
                {_.upperFirst(templateName)}{' '}
                <Badge status="success">
                  {i18n.translate(
                    'AdminHTML.ShopifyIntegration.SelectTemplate.badgeCurrentTemplate'
                  )}
                </Badge>
              </div>
              <div className="description">
                Template ID: {templateId}{' '}
                <span className="copy">
                  <Button
                    icon={DuplicateMinor}
                    plain
                    onClick={() => copyClipBoard(i18n, templateId)}
                  />
                </span>
              </div>
              <div className="description">
                Version: {templateVersion} (Latest version: {boostTemplateVersion})
              </div>
              <div className="description">Last saved: {lastSavedAt}</div>
            </div>

            {/* Actions */}
            <div className="action-page-update">
              <ButtonGroup>
                {/* Change, Manage template */}
                <PopOverActionButton
                  themes={themes}
                  selectedThemeId={selectedThemeId}
                  onChangeTemplate={this.handleChangeModal}
                  onManageTemplate={this.handleGoToManageTemplate}
                />

                {/* Upgrade button */}
                {process.env.REACT_APP_ENV !== 'production' && (
                  <div className="btn-upgrade">
                    <Button
                      disabled={!hasUpdate}
                      loading={upgradingTheme}
                      onClick={() => {
                        this.handleThemeUpgrade();
                      }}>
                      Upgrade
                    </Button>
                  </div>
                )}

                {/* Customization */}
                <Button primary onClick={this.handleCustomizeTemplate}>
                  {i18n.translate('AdminHTML.ShopifyIntegration.Actions.customize')}
                </Button>
              </ButtonGroup>
            </div>
          </div>
        )}
      </div>
    );
  };

  renderWidgetIntegrationWithAppEmbed = () => {
    const {
      i18n,
      selectedThemeId,
      checkingTheme,
      getAllThemesLoading,
      getTemplateInstancesLoading
    } = this.props;
    const isLoading = checkingTheme || getAllThemesLoading || getTemplateInstancesLoading;

    return (
      <div className="app-embed-instance">
        {/* Heading */}
        <div className="heading">
          {i18n.translate('AdminHTML.ShopifyIntegration.UpdateInstance.appEmbedHeading')}
        </div>

        {/* Title */}
        <div className="title">
          {i18n.translate('AdminHTML.ShopifyIntegration.UpdateInstance.appEmbedInstruction')}
        </div>

        {isLoading ? (
          <div
            style={{
              marginBottom: '40px'
            }}>
            <SkeletonDisplayText size="extraLarge" />
          </div>
        ) : (
          <div className="instance page-update">
            {/* Image thumbnail represents specific theme lib */}
            <img className="thumbnail" src={BoostLogo} width={40} height={40} alt="" />

            {/* Template info */}
            <div className="details">
              <div className="info-title">
                {i18n.translate('AdminHTML.ShopifyIntegration.UpdateInstance.appEmbedName')}
              </div>
              <div className="description">
                {i18n.translate('AdminHTML.ShopifyIntegration.UpdateInstance.appEmbedBoostName')}
              </div>
            </div>

            {/* View settings */}
            <div className="view-settings">
              <Link
                external
                onClick={() => {
                  navigateToShopifyCustomizeSSR(selectedThemeId);
                }}>
                <span>{'View settings'}</span>
                <Icon source={ExternalMinor} color="interactive" />
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  };

  renderGuideSetupWidgets = () => {
    const { i18n, checkingTheme, getAllThemesLoading, getTemplateInstancesLoading } = this.props;
    const { isLoadingAddCollection, isLoadingAddSearch, isEnablingISW } = this.state;
    const isLoading = checkingTheme || getAllThemesLoading || getTemplateInstancesLoading;
    const isLiveTheme = this.checkLiveTheme();

    const MAPPING_BUTTON_TEXT = {
      [TEMPLATE_FEATURE.COLLECTION]: i18n.translate('AdminHTML.ShopifyIntegration.Actions.add'),
      [TEMPLATE_FEATURE.SEARCH]: i18n.translate('AdminHTML.ShopifyIntegration.Actions.add'),
      [TEMPLATE_FEATURE.ISW]: i18n.translate('AdminHTML.ShopifyIntegration.Actions.enable'),
      [TEMPLATE_FEATURE.RECOMMENDATION]: i18n.translate(
        'AdminHTML.ShopifyIntegration.Actions.manage'
      )
    };

    const tooltipAction = {
      [TEMPLATE_FEATURE.COLLECTION]: i18n.translate(
        'AdminHTML.ShopifyIntegration.Actions.addWidget'
      ),
      [TEMPLATE_FEATURE.SEARCH]: i18n.translate('AdminHTML.ShopifyIntegration.Actions.addWidget'),
      [TEMPLATE_FEATURE.ISW]: i18n.translate('AdminHTML.ShopifyIntegration.Actions.enableWidget'),
      [TEMPLATE_FEATURE.RECOMMENDATION]: i18n.translate(
        'AdminHTML.ShopifyIntegration.Actions.manageWidget'
      )
    };

    return (
      <div className="setup-widgets">
        <div className="heading">
          <span>{i18n.translate('AdminHTML.ShopifyIntegration.UpdateInstance.guideHeading')}</span>
        </div>
        <div className="title">
          <TextStyle variation="subdued">
            {i18n.translate('AdminHTML.ShopifyIntegration.SetupWidgets.SetupIntroduction_v2')}
          </TextStyle>
        </div>

        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {_.map([1, 2, 3, 4], item => {
              return (
                <div key={item} className="item">
                  <SkeletonDisplayText size="extraLarge" />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="features">
            {_.map(WIDGET_FEATURES, feature => {
              return (
                <div key={feature.name} className="feature-item">
                  <div>{feature.name}</div>
                  <div className="feature-action">
                    <Tooltip
                      content={i18n.translate(
                        'AdminHTML.ShopifyIntegration.SetupWidgets.ViewGuideTooltip'
                      )}
                      preferredPosition="above">
                      <Button onClick={() => this.handleGoToFeaturePage(feature.pathId)}>
                        <Icon source={ReadTimeMinor} color="interactive" />
                      </Button>
                    </Tooltip>
                    {((isLiveTheme && feature.pathId === TEMPLATE_FEATURE.RECOMMENDATION) ||
                      feature.pathId !== TEMPLATE_FEATURE.RECOMMENDATION) && (
                      <Tooltip
                        content={tooltipAction[feature.pathId]}
                        preferredPosition="above"
                        activatorWrapper="div">
                        <Button
                          loading={
                            this.pathId === feature.pathId &&
                            (isLoadingAddCollection || isLoadingAddSearch || isEnablingISW)
                          }
                          onClick={() => this.handleAction(feature.pathId, feature.featureId)}>
                          {MAPPING_BUTTON_TEXT[feature.pathId]}
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="setup-guide">
          <div className="heading">
            <span>{i18n.translate('AdminHTML.ShopifyIntegration.SetupGuide.title')}</span>
          </div>
          <p className="title">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ShopifyIntegration.SetupGuide.subtitle')}
            </TextStyle>
          </p>

          <div className="setup-guide__wrapper">
            <div className="setup-guide__item">
              <Link url={URL['BoostTemplateWidgets']} external>
                <Icon source={ThemesMajor} color="primary" />
                <p>
                  {i18n.translate('AdminHTML.ShopifyIntegration.SetupGuide.boostTemplateWidget')}
                </p>
              </Link>
            </div>
            <div className="setup-guide__item">
              <Link url={URL['SetupWidgetForTheme']} external>
                <Icon source={SettingsMajor} color="primary" />
                <p>
                  {i18n.translate('AdminHTML.ShopifyIntegration.SetupGuide.setupWidgetForTheme')}
                </p>
              </Link>
            </div>
            <div className="setup-guide__item">
              <Link url={URL['RemoveWidgetForTheme']} external>
                <Icon source={DeleteMajor} color="primary" />
                <p>
                  {i18n.translate('AdminHTML.ShopifyIntegration.SetupGuide.removeWidgetForTheme')}
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  };

  handleGoBack = () => {
    // Go back to theme
    history.push(routes.shopifyIntegration.view());
  };

  /* Change template modal */
  handleChangeModal = () => {
    const { openModal } = this.state;
    this.setState({ openModal: !openModal });
  };

  handleConfirmModal = async (templateId, themeNameLib, isBoostTemplate) => {
    const {
      i18n,
      themeIntegrationSetup,
      selectedThemeId,
      addCreatedTemplateInstance,
      themes,
      templateInstances
    } = this.props;
    const { integratedTemplateInstance } = this.state;
    const { templateId: oldTemplateId } = integratedTemplateInstance;

    // Call setup and then add new created template instance to state
    await themeIntegrationSetup(selectedThemeId, templateId, themeNameLib, isBoostTemplate).then(
      async response => {
        const { type = '' } = response;

        if (type === 'SI_THEME_INTEGRATION_SETUP_SUCCESS') {
          const createdTemplateInstance = this.props.createdTemplateInstance;

          if (isBoostTemplate && !_.isEmpty(createdTemplateInstance)) {
            addCreatedTemplateInstance(createdTemplateInstance, isBoostTemplate);
          }

          // Track Full Story event
          const themeDetails = _.find(themes, { id: selectedThemeId });
          trackFSEvent('Change a Template', {
            feature: 'SSR Theme Setup',
            themeName: themeDetails?.name,
            templateName: createdTemplateInstance.templateName,
            templateId: createdTemplateInstance.templateId,
            oldTemplateId: oldTemplateId
          });

          const hasUpdate = !_.isNil(this.hashTemplateUpdate[templateId])
            ? this.hashTemplateUpdate[templateId]
            : Boolean(_.find(templateInstances, { templateId })?.hasUpdate) || false;

          // Save state
          this.setState({ integratedTemplateInstance: createdTemplateInstance, hasUpdate });

          // Show toast message
          showSABToast(
            i18n.translate(
              'AdminHTML.ShopifyIntegration.UpdateInstance.toastTemplateChangeSuccess'
            ),
            false
          );

          // Close modal
          this.setState({ openModal: false });

          // Show template changed modal
          this.handleChangeTemplateModal();
        }
      }
    );
  };

  /* End Change template modal */

  handleChangeTemplateModal = () => {
    const { openTemplateChangedModal } = this.state;
    this.setState({ openTemplateChangedModal: !openTemplateChangedModal });
  };

  /** Customize template */
  handleCustomizeTemplateModal = () => {
    const { openCustomizeModal } = this.state;
    this.setState({ openCustomizeModal: !openCustomizeModal });
  };

  handleGoToManageTemplate = () => {
    const { selectedThemeId, themes, updateMultiFieldsReducer } = this.props;
    const { integratedTemplateInstance = {} } = this.state;
    const templateId = _.get(integratedTemplateInstance, 'templateId', '');

    // Get theme details
    const themeDetails = _.find(themes, { id: selectedThemeId });
    const themeRole = _.get(themeDetails, 'role', '');
    const isLiveTheme = themeRole === 'main';

    updateMultiFieldsReducer({
      currentTemplateId: templateId,
      isLiveTheme
    });
    history.push({
      pathname: routes.shopifyIntegration.manageTemplateInstance()
    });
  };

  handleGoToFeaturePage = pathId => {
    const { selectedThemeId, getAllThemes } = this.props;
    const { integratedTemplateInstance = {} } = this.state;

    getAllThemes();
    // Get themeId, templateId
    const templateId = _.get(integratedTemplateInstance, 'templateId', '');

    history.push({
      pathname: routes.shopifyIntegration.guide(selectedThemeId, templateId, pathId)
    });
  };

  handleThemeUpgrade = async () => {
    const { selectedThemeId, themeIntegrationUpgrade } = this.props;
    const { integratedTemplateInstance = {} } = this.state;

    // Get themeId, templateId
    const templateId = _.get(integratedTemplateInstance, 'templateId', '');

    // Call setup and then add new created template instance to state
    await themeIntegrationUpgrade(selectedThemeId, templateId, 'latest').then(async response => {
      const { type = '' } = response;

      if (type === 'SI_THEME_INTEGRATION_UPGRADE_SUCCESS') {
        const upgradedTemplateInstance = this.props.upgradedTemplateInstance;

        // Save state
        this.setState({ integratedTemplateInstance: upgradedTemplateInstance, hasUpdate: false });
        _.set(this.hashTemplateUpdate, templateId, false);

        // Show toast message
        showSABToast('Upgrade successfully.', false);
      }
    });
  };

  handleCustomizeTemplate = () => {
    const { selectedThemeId, themes } = this.props;
    const { integratedTemplateInstance } = this.state;
    const { templateId, templateName } = integratedTemplateInstance;

    // Get theme details
    const themeDetails = _.find(themes, { id: selectedThemeId });
    const themeRole = _.get(themeDetails, 'role', '');
    const isLiveTheme = themeRole === 'main';

    // Check link with live theme
    const linkedWithLiveTheme = this.checkLinkedWithLiveTheme(integratedTemplateInstance);

    // Track Full Story event
    trackFSEvent('Customize a Template', {
      feature: 'SSR Theme Setup',
      selectedTemplate: templateName,
      templateId
    });

    // Show warning modal: Unpublished theme and Template linked with live theme
    if (!isLiveTheme && linkedWithLiveTheme) {
      this.setState({
        openCustomizeModal: true
      });
    } else {
      history.push({ pathname: routes.shopifyIntegrationSettings.visualEditor(templateId) });
      return;
    }
  };

  checkLinkedWithLiveTheme = templateInstance => {
    const { themes = [] } = this.props;
    const themeIds = _.get(templateInstance, 'themeIds', []);

    let linkedWithLiveTheme = false;
    for (const themeId of themeIds) {
      if (linkedWithLiveTheme) break;

      const themeDetails = _.find(themes, { id: themeId });
      if (!_.isEmpty(themeDetails) && themeDetails.role === 'main') {
        linkedWithLiveTheme = true;
      }
    }

    return linkedWithLiveTheme;
  };

  handleConfirmCustomizeModal = () => {
    const { integratedTemplateInstance = {} } = this.state;
    const { templateId } = integratedTemplateInstance;
    const {
      i18n,
      duplicateTemplateInstance,
      themeIntegrationSetup,
      selectedThemeId,
      templateInstances
    } = this.props;

    if (!templateId) {
      return;
    }

    duplicateTemplateInstance(templateId).then(responseDuplicate => {
      const { type = '' } = responseDuplicate;
      const duplicateTemplateId = _.get(responseDuplicate, 'payload.data.templateId', '');
      const themeNameLib = _.get(responseDuplicate, 'payload.data.themeNameLib', '');

      if (type === 'SI_DUPLICATE_TEMPLATE_SUCCESS') {
        // Call setup and then add new created template instance to state
        themeIntegrationSetup(selectedThemeId, duplicateTemplateId, themeNameLib, false)
          .then(response => {
            const { type = '' } = response;

            if (type === 'SI_THEME_INTEGRATION_SETUP_SUCCESS') {
              // const createdTemplateInstance = this.props.createdTemplateInstance;

              // Keep status of original theme -> use templateId instead of duplicateTemplateId
              const hasUpdate = !_.isNil(this.hashTemplateUpdate[templateId])
                ? this.hashTemplateUpdate[templateId]
                : Boolean(_.find(templateInstances, { templateId })?.hasUpdate) || false;

              // Save state
              this.setState({
                integratedTemplateInstance: _.get(response, 'payload.data'),
                openCustomizeModal: false,
                hasUpdate
              });

              setTimeout(() => {
                this.setState({
                  openTemplateChangedModal: true
                });
              }, 300);

              showSABToast(
                i18n.translate(
                  'AdminHTML.ShopifyIntegration.ManageTemplateInstance.duplicateTemplateSuccess'
                ),
                false
              );
            }
          })
          .catch(error => {
            console.log('ERROR', error);
          });
      }
    });
  };
  /** End Customize template */

  handleAction = async (pathId, featureId) => {
    this.pathId = pathId;
    this.featureId = featureId;
    const { themes, selectedThemeId, themeIntegrationIntegrateFeature } = this.props;
    const themeInfo = _.find(themes, { id: selectedThemeId });
    const isLiveTheme = themeInfo?.role === 'main';

    switch (pathId) {
      case TEMPLATE_FEATURE.COLLECTION:
      case TEMPLATE_FEATURE.SEARCH:
        if (isLiveTheme) {
          this.setState({
            openWarningAddWidgetLiveTheme: true
          });
        } else {
          this.processAddWidget();
        }
        break;
      case TEMPLATE_FEATURE.ISW:
        this.setState({
          isEnablingISW: true
        });

        const result = await themeIntegrationIntegrateFeature(selectedThemeId, featureId);
        const state = {
          isEnablingISW: false
        };

        if (result?.type === SI_THEME_INTEGRATION_FEATURE_SUCCESS) {
          state.openEnableWidgetSuccess = true;
        } else {
          state.openEnableWidgetFail = true;
        }

        this.setState(state);
        break;
      case TEMPLATE_FEATURE.RECOMMENDATION:
        const recommendationURL = getFullAppUrlV2(routes.recommendationWidgets.list(1)); // Draft tab
        window.open(recommendationURL, '_blank');
        break;
      default:
        break;
    }
  };

  handleCloseWarningAddWidgetLive = () => {
    this.setState({
      openWarningAddWidgetLiveTheme: false
    });
  };

  processAddWidget = async () => {
    const { selectedThemeId, themeIntegrationIntegrateFeature } = this.props;

    const isLoadingField =
      this.pathId === TEMPLATE_FEATURE.COLLECTION ? 'isLoadingAddCollection' : 'isLoadingAddSearch';

    this.setState({
      [isLoadingField]: true
    });
    const result = await themeIntegrationIntegrateFeature(selectedThemeId, this.featureId);
    const state = {
      [isLoadingField]: false
    };

    if (result?.type === SI_THEME_INTEGRATION_FEATURE_SUCCESS) {
      state.openAddWidgetSuccess = true;
    } else {
      const errorCode = _.get(result, 'payload.data.errorCode');
      switch (errorCode) {
        case ERROR_CODE.REACH_LIMIT: {
          state.openErrorLimitSection = true;
          break;
        }
        case ERROR_CODE.ALREADY_ADDED: {
          state.openWidgetAlreadyAdded = true;
          break;
        }
        default:
          state.openErrorModal = true;
      }
    }

    this.setState(state);
  };

  handleAddWidget = async () => {
    this.processAddWidget();
    this.setState({
      openWarningAddWidgetLiveTheme: false
    });
  };

  handleCloseErrorLimitSection = () => {
    this.setState({
      openErrorLimitSection: false
    });
  };

  handleCloseErrorModal = () => {
    this.setState({
      openErrorModal: false
    });
  };

  handleCloseSuccessModal = () => {
    this.setState({
      openAddWidgetSuccess: false
    });
  };

  handleCloseEnableWidgetSuccessModal = () => {
    this.setState({
      openEnableWidgetSuccess: false
    });
  };

  handleCloseEnableWidgetFailModal = () => {
    this.setState({
      openEnableWidgetFail: false
    });
  };

  handleCloseWidgetAlreadyAdded = () => {
    this.setState({
      openWidgetAlreadyAdded: false
    });
  };

  checkLiveTheme = () => {
    const { themes, selectedThemeId } = this.props;
    const themeDetails = _.find(themes, { id: selectedThemeId });
    const themeRole = _.get(themeDetails, 'role', '');
    return themeRole === 'main';
  };
}

const mapStateToProps = (state, props) => {
  const { shopifyIntegrationTheme = {}, templateInstance = {}, themeIntegration = {} } = state;

  const themes = _.get(shopifyIntegrationTheme, 'allThemes', []);
  const boostTemplates = _.get(templateInstance, 'boostTemplates', []);
  const templateInstances = _.get(templateInstance, 'templateInstances', []);

  // Theme integration setup
  const integratingTheme = _.get(themeIntegration, 'integratingTheme', false);
  const createdTemplateInstance = _.get(themeIntegration, 'createdTemplateInstance', {});

  // Theme integration upgrade
  const upgradingTheme = _.get(themeIntegration, 'upgradingTheme', false);
  const upgradedTemplateInstance = _.get(themeIntegration, 'upgradedTemplateInstance', {});
  const integratedTemplateInstance = _.get(themeIntegration, 'templateInstance', {});

  const duplicateTemplateLoading = _.get(templateInstance, 'duplicateTemplateLoading', false);
  const duplicatedTemplateInstance = _.get(templateInstance, 'duplicatedTemplateInstance', {});

  return {
    emailSupport: _.get(state.oauth, 'emailSupport'),

    // All shopify themes
    themes,
    selectedThemeId: _.get(themeIntegration, 'selectedThemeId', ''),
    checkingTheme: _.get(themeIntegration, 'checkingTheme', false),
    getAllThemesLoading: _.get(shopifyIntegrationTheme, 'getAllThemesLoading', false),
    getBoostTemplatesLoading: _.get(templateInstance, 'getBoostTemplatesLoading', false),
    getTemplateInstancesLoading: _.get(templateInstance, 'getTemplateInstancesLoading', false),
    isThemeDeleted: _.get(integratedTemplateInstance, 'isDeleted', false),
    integratedTemplateInstance,

    // Boost template instance, store template instance
    boostTemplates,
    templateInstances,

    // Theme integration setup
    integratingTheme,
    createdTemplateInstance,

    // Duplicate template instance
    duplicateTemplateLoading,
    duplicatedTemplateInstance,

    // Theme integration upgrade
    upgradingTheme,
    upgradedTemplateInstance
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    getAllThemes: () => dispatch(shopifyIntegrationThemeAction.getAllThemes(ownProps.i18n)),
    getBoostTemplateInstances: () =>
      dispatch(templateInstanceAction.getBoostTemplateInstances(ownProps.i18n)),
    getTemplateInstances: () =>
      dispatch(templateInstanceAction.getTemplateInstances(ownProps.i18n)),
    themeIntegrationSetup: (themeId, templateId, themeNameLib, isBoostTemplate) =>
      dispatch(
        themeIntegrationAction.themeIntegrationSetup(
          ownProps.i18n,
          themeId,
          templateId,
          themeNameLib,
          isBoostTemplate
        )
      ),
    themeIntegrationUpgrade: (themeId, templateId, version) =>
      dispatch(
        themeIntegrationAction.themeIntegrationUpgrade(ownProps.i18n, themeId, templateId, version)
      ),
    themeIntegrationIntegrateFeature: (themeId, feature) =>
      dispatch(
        themeIntegrationAction.themeIntegrationIntegrateFeature(themeId, feature, ownProps.i18n)
      ),
    addCreatedTemplateInstance: (templateInstance, isBoostTemplate) =>
      dispatch(
        templateInstanceAction.addCreatedTemplateInstance(templateInstance, isBoostTemplate)
      ),
    checkThemeIntegrationStatus: themeId =>
      dispatch(themeIntegrationAction.checkThemeIntegrationStatus(ownProps.i18n, themeId)),
    updateFieldReducer: (fieldName, data) =>
      dispatch(themeIntegrationAction.handleUpdateField(fieldName, data)),
    updateMultiFieldsReducer: data =>
      dispatch(themeIntegrationAction.handleUpdateMultiFields(data)),
    duplicateTemplateInstance: templateId =>
      dispatch(templateInstanceAction.duplicateTemplateInstance(ownProps.i18n, templateId))
  };
};

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(ShopifyIntegrationScreenUpdate)
);
